/**
 * 從 URL 取得 groupId 的工具函數
 * 使用 query parameter（與 member 的 tenantId 一致）
 * React Router 的 navigate() 會自動保留 query parameters
 */

/**
 * SessionStorage key for groupId backup
 */
const GROUP_ID_STORAGE_KEY = 'lite_group_id_backup';

/**
 * 是否為 Square 路徑（/lite/shop/:suffix/...），此模式下不使用 groupId
 * @param pathname - 可選，未傳則使用 window.location.pathname（便於測試或傳入 location.pathname）
 */
export function isSquarePath(pathname?: string): boolean {
  const p =
    pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  // 使用 includes 避免部署時 basePath 變動（例如 /member/lite/shop/...），只要路徑中含有 /lite/shop/ 即視為 Square 模式
  return p.includes('/lite/shop/');
}

/**
 * 從當前 URL 取得 groupId
 * 優先從 query parameter 取得，如果沒有則從 sessionStorage 取得（作為備份）
 * Square 路徑（/lite/shop/:suffix）不使用 groupId，且會清除 URL 上的 groupId
 * @returns groupId 或 null
 */
export function getGroupIdFromUrl(): string | null {
  // E2E 模式：不注入 mock groupId，讓 status 為 not_in_group，僅靠 tenantId 進入預購／付款回調
  if (import.meta.env.VITE_E2E === 'true' || import.meta.env.MODE === 'test') {
    return null;
  }

  // Square 模式不使用 groupId，且避免把 groupId 寫入 URL
  if (isSquarePath()) {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('groupId') || urlParams.has('lineGroupId')) {
      urlParams.delete('groupId');
      urlParams.delete('lineGroupId');
      const newSearch = urlParams.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}${window.location.hash}`
        : window.location.pathname + window.location.hash;
      window.history.replaceState(null, '', newUrl);
    }
    return null;
  }

  // 從 query parameter 取得（與 member 的 tenantId 一致）
  const urlParams = new URLSearchParams(window.location.search);
  const groupId =
    urlParams.get('lineGroupId') || urlParams.get('groupId') || null;

  // 如果從 URL 取得 groupId，保存到 sessionStorage 作為備份
  if (groupId) {
    try {
      sessionStorage.setItem(GROUP_ID_STORAGE_KEY, groupId);
    } catch (error) {
      console.warn(
        '[getGroupIdFromUrl] 無法保存 groupId 到 sessionStorage:',
        error
      );
    }
    return groupId;
  }

  // 如果 URL 中沒有，嘗試從 sessionStorage 取得（作為備份）
  try {
    const backupGroupId = sessionStorage.getItem(GROUP_ID_STORAGE_KEY);
    if (backupGroupId) {
      console.log(
        '[getGroupIdFromUrl] 從 sessionStorage 取得備份 groupId:',
        backupGroupId
      );
      // 如果從 sessionStorage 取得，同時更新 URL query parameter
      const newSearch = new URLSearchParams(window.location.search);
      newSearch.set('groupId', backupGroupId);
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}?${newSearch.toString()}${window.location.hash}`
      );
      return backupGroupId;
    }
  } catch (error) {
    console.warn(
      '[getGroupIdFromUrl] 無法從 sessionStorage 讀取 groupId:',
      error
    );
  }

  // 開發環境：如果無法取得 groupId，使用 mock group id
  const isDevelopment =
    import.meta.env.MODE === 'development' || import.meta.env.DEV === true;
  if (isDevelopment) {
    const mockGroupId =
      import.meta.env.VITE_MOCK_LINE_GROUP_ID ||
      'Ca56f94637cec434d27582291d19f510f';
    console.log(
      '[getGroupIdFromUrl] 🧪 開發環境：使用 mock groupId:',
      mockGroupId
    );
    // 自動更新 URL 和 sessionStorage
    try {
      const newSearch = new URLSearchParams(window.location.search);
      newSearch.set('groupId', mockGroupId);
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}?${newSearch.toString()}${window.location.hash}`
      );
      sessionStorage.setItem(GROUP_ID_STORAGE_KEY, mockGroupId);
    } catch (error) {
      console.warn('[getGroupIdFromUrl] 無法保存 mock groupId:', error);
    }
    return mockGroupId;
  }

  return null;
}

/**
 * 將 groupId 附加到 URL（用於路由跳轉時保留 groupId）
 * Square 路徑（/lite/shop/:suffix）不附加 groupId
 * @param path 目標路徑
 * @param groupId groupId（如果為 null，會從當前 URL 取得）
 * @returns 包含 groupId 的完整路徑（使用 query parameter，與 member 的 tenantId 一致）
 */
export function preserveGroupIdInPath(
  path: string,
  groupId?: string | null
): string {
  if (path.startsWith('/lite/shop/')) {
    return path;
  }
  const targetGroupId = groupId || getGroupIdFromUrl();

  if (!targetGroupId) {
    return path;
  }

  // 使用 query parameter（與 member 的 tenantId 一致）
  // React Router 的 navigate() 會自動保留 query parameters
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}groupId=${encodeURIComponent(targetGroupId)}`;
}

/**
 * 取得 Lite 返回路徑：Square 回 /lite/shop/:suffix，Group 回 /lite?groupId=...
 * @param params.suffix - 商家 URL suffix（Square 脈絡）
 * @param params.groupId - LINE 群組 ID（Group 脈絡）
 * @returns 返回路徑
 */
export function getLiteReturnPath(params: {
  suffix?: string | null;
  groupId?: string | null;
}): string {
  const { suffix, groupId } = params;
  if (suffix?.trim()) return `/lite/shop/${encodeURIComponent(suffix.trim())}`;
  if (groupId?.trim())
    return `/lite?groupId=${encodeURIComponent(groupId.trim())}`;
  return '/lite';
}
